import { memo, useMemo } from "react"
import { cn } from "@/lib/utils/shadcn"
import {
    SvgLayerFilterDefs,
    layerNeedsSvgFilter,
    normalizeLayerFilters,
    blendModeToCss,
} from "./layerFilters"

function layerFilterDomId(layerId) {
    return `lf-${String(layerId ?? 'x').replace(/[^a-zA-Z0-9_-]/g, '_')}`
}

const HANDLE_SIZE = 10
const CROP_HANDLE_SIZE = 8
const ROTATION_HANDLE_OFFSET = 30
const ROTATION_HANDLE_RADIUS = 6

function pointsToSvgPath(points) {
    if (!points || points.length === 0) return ''
    if (points.length === 1) {
        const p = points[0]
        return `M${p.x},${p.y}L${p.x},${p.y}`
    }
    let d = `M${points[0].x},${points[0].y}`
    for (let i = 1; i < points.length; i++) {
        const prev = points[i - 1]
        const curr = points[i]
        const mx = (prev.x + curr.x) / 2
        const my = (prev.y + curr.y) / 2
        d += `Q${prev.x},${prev.y},${mx},${my}`
    }
    const last = points[points.length - 1]
    d += `L${last.x},${last.y}`
    return d
}

function getPathBounds(el) {
    if (!el.points?.length) return null
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    let maxW = 0
    for (const p of el.points) {
        if (p.x < minX) minX = p.x
        if (p.y < minY) minY = p.y
        if (p.x > maxX) maxX = p.x
        if (p.y > maxY) maxY = p.y
        if ((p.w ?? 0) > maxW) maxW = p.w ?? 0
    }
    const pad = Math.max(maxW, el.size) / 2 + 4
    return { minX: minX - pad, minY: minY - pad, maxX: maxX + pad, maxY: maxY + pad }
}

function getElementBounds(el, canvasWidth, canvasHeight) {
    if (el.kind === 'path') {
        return getPathBounds(el)
    }
    if (el.kind === 'image') {
        const x = el.x ?? 0
        const y = el.y ?? 0
        const w = canvasWidth * (el.scaleX ?? 1)
        const h = canvasHeight * (el.scaleY ?? 1)
        const rot = el.rotation ?? 0
        const x1 = Math.min(x, x + w), y1 = Math.min(y, y + h)
        const x2 = Math.max(x, x + w), y2 = Math.max(y, y + h)
        if (rot === 0) {
            return { minX: x1, minY: y1, maxX: x2, maxY: y2 }
        }
        const cx = (x1 + x2) / 2
        const cy = (y1 + y2) / 2
        const corners = [
            { x: x1, y: y1 }, { x: x2, y: y1 },
            { x: x2, y: y2 }, { x: x1, y: y2 },
        ]
        const rad = (rot * Math.PI) / 180
        const cos = Math.cos(rad), sin = Math.sin(rad)
        let rMinX = Infinity, rMinY = Infinity, rMaxX = -Infinity, rMaxY = -Infinity
        for (const c of corners) {
            const dx = c.x - cx, dy = c.y - cy
            const rx = cx + dx * cos - dy * sin
            const ry = cy + dx * sin + dy * cos
            if (rx < rMinX) rMinX = rx
            if (ry < rMinY) rMinY = ry
            if (rx > rMaxX) rMaxX = rx
            if (ry > rMaxY) rMaxY = ry
        }
        return { minX: rMinX, minY: rMinY, maxX: rMaxX, maxY: rMaxY }
    }
    return null
}

function getElementLocalBounds(el, canvasWidth, canvasHeight) {
    if (el.kind === 'path') {
        return getPathBounds(el)
    }
    if (el.kind === 'image') {
        const x = el.x ?? 0
        const y = el.y ?? 0
        const w = canvasWidth * (el.scaleX ?? 1)
        const h = canvasHeight * (el.scaleY ?? 1)
        return { minX: Math.min(x, x + w), minY: Math.min(y, y + h), maxX: Math.max(x, x + w), maxY: Math.max(y, y + h) }
    }
    return null
}

function getMultiElementBounds(elements, canvasWidth, canvasHeight) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    for (const el of elements) {
        const b = getElementBounds(el, canvasWidth, canvasHeight)
        if (!b) continue
        if (b.minX < minX) minX = b.minX
        if (b.minY < minY) minY = b.minY
        if (b.maxX > maxX) maxX = b.maxX
        if (b.maxY > maxY) maxY = b.maxY
    }
    if (minX === Infinity) return null
    return { minX, minY, maxX, maxY }
}

function getBoundsCenter(bounds) {
    return {
        x: (bounds.minX + bounds.maxX) / 2,
        y: (bounds.minY + bounds.maxY) / 2,
    }
}

function getHandlePositions(bounds) {
    if (!bounds) return []
    const { minX, minY, maxX, maxY } = bounds
    return [
        { id: 'tl', x: minX, y: minY, cursor: 'nwse-resize' },
        { id: 'tr', x: maxX, y: minY, cursor: 'nesw-resize' },
        { id: 'bl', x: minX, y: maxY, cursor: 'nesw-resize' },
        { id: 'br', x: maxX, y: maxY, cursor: 'nwse-resize' },
    ]
}

function getRotationHandlePos(bounds) {
    if (!bounds) return null
    const cx = (bounds.minX + bounds.maxX) / 2
    return { x: cx, y: bounds.minY - ROTATION_HANDLE_OFFSET }
}

function getCropRect(el, canvasWidth, canvasHeight) {
    if (el.kind !== 'image') return null
    const local = getElementLocalBounds(el, canvasWidth, canvasHeight)
    if (!local) return null
    return {
        minX: local.minX + (el.cropLeft ?? 0),
        minY: local.minY + (el.cropTop ?? 0),
        maxX: local.maxX - (el.cropRight ?? 0),
        maxY: local.maxY - (el.cropBottom ?? 0),
    }
}

function hasCrop(el) {
    if (el.kind !== 'image') return false
    return (el.cropTop ?? 0) !== 0 || (el.cropRight ?? 0) !== 0 ||
        (el.cropBottom ?? 0) !== 0 || (el.cropLeft ?? 0) !== 0
}

function getCropHandlePositions(cropRect) {
    if (!cropRect) return []
    const { minX, minY, maxX, maxY } = cropRect
    const mx = (minX + maxX) / 2
    const my = (minY + maxY) / 2
    return [
        { id: 'crop-tl', x: minX, y: minY, cursor: 'nwse-resize' },
        { id: 'crop-t', x: mx, y: minY, cursor: 'ns-resize' },
        { id: 'crop-tr', x: maxX, y: minY, cursor: 'nesw-resize' },
        { id: 'crop-r', x: maxX, y: my, cursor: 'ew-resize' },
        { id: 'crop-br', x: maxX, y: maxY, cursor: 'nwse-resize' },
        { id: 'crop-b', x: mx, y: maxY, cursor: 'ns-resize' },
        { id: 'crop-bl', x: minX, y: maxY, cursor: 'nesw-resize' },
        { id: 'crop-l', x: minX, y: my, cursor: 'ew-resize' },
    ]
}

function renderImageSvg(img, canvasWidth, canvasHeight, { isCropTarget = false, cropEditRect = null } = {}) {
    const ix = img.x ?? 0
    const iy = img.y ?? 0
    const isx = img.scaleX ?? 1
    const isy = img.scaleY ?? 1
    const rot = img.rotation ?? 0
    const local = getElementLocalBounds(img, canvasWidth, canvasHeight)
    const center = local ? getBoundsCenter(local) : { x: ix + canvasWidth * isx / 2, y: iy + canvasHeight * isy / 2 }
    let transform = `translate(${ix},${iy}) scale(${isx},${isy})`
    if (rot !== 0) {
        transform = `rotate(${rot} ${center.x} ${center.y}) ${transform}`
    }

    const activeCrop = isCropTarget ? cropEditRect : (hasCrop(img) ? getCropRect(img, canvasWidth, canvasHeight) : null)
    const clipId = activeCrop ? `crop-clip-${img._id}` : null
    const clipRotTransform = rot !== 0 ? `rotate(${rot} ${center.x} ${center.y})` : undefined

    if (isCropTarget && cropEditRect && local) {
        return (
            <g key={img._id}>
                <g transform={transform} opacity={0.3}>
                    <image href={img.imageUrl} width={canvasWidth} height={canvasHeight} preserveAspectRatio="none" />
                </g>
                <defs>
                    <clipPath id={clipId}>
                        <rect
                            x={cropEditRect.minX} y={cropEditRect.minY}
                            width={cropEditRect.maxX - cropEditRect.minX}
                            height={cropEditRect.maxY - cropEditRect.minY}
                            transform={clipRotTransform}
                        />
                    </clipPath>
                </defs>
                <g clipPath={`url(#${clipId})`}>
                    <g transform={transform}>
                        <image href={img.imageUrl} width={canvasWidth} height={canvasHeight} preserveAspectRatio="none" />
                    </g>
                </g>
            </g>
        )
    }

    if (activeCrop) {
        return (
            <g key={img._id}>
                <defs>
                    <clipPath id={clipId}>
                        <rect
                            x={activeCrop.minX} y={activeCrop.minY}
                            width={activeCrop.maxX - activeCrop.minX}
                            height={activeCrop.maxY - activeCrop.minY}
                            transform={clipRotTransform}
                        />
                    </clipPath>
                </defs>
                <g clipPath={`url(#${clipId})`}>
                    <g transform={transform}>
                        <image href={img.imageUrl} width={canvasWidth} height={canvasHeight} preserveAspectRatio="none" />
                    </g>
                </g>
            </g>
        )
    }

    return (
        <g key={img._id} transform={transform}>
            <image href={img.imageUrl} width={canvasWidth} height={canvasHeight} preserveAspectRatio="none" />
        </g>
    )
}

function hasVariableWidth(points) {
    if (!points || points.length < 2) return false
    return points[0].w != null
}

function renderPathSvg(shape, canvasWidth, canvasHeight) {
    const rot = shape.rotation ?? 0
    const b = rot !== 0 ? getElementBounds(shape, canvasWidth, canvasHeight) : null
    const c = b ? getBoundsCenter(b) : null
    const transform = c ? `rotate(${rot} ${c.x} ${c.y})` : undefined
    const pts = shape.points

    const renderInner = () => {
        if (!hasVariableWidth(pts)) {
            return (
                <path
                    d={pointsToSvgPath(pts)}
                    stroke={shape.color}
                    strokeWidth={shape.size}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                    transform={transform}
                />
            )
        }
        return (
            <g transform={transform}>
                {pts.length === 1 && (
                    <circle cx={pts[0].x} cy={pts[0].y} r={(pts[0].w ?? shape.size) / 2} fill={shape.color} />
                )}
                {pts.length >= 2 && pts.map((pt, i) => {
                    if (i === 0) return null
                    const prev = pts[i - 1]
                    const w = (prev.w + pt.w) / 2
                    const mx = (prev.x + pt.x) / 2
                    const my = (prev.y + pt.y) / 2
                    return (
                        <path
                            key={i}
                            d={`M${prev.x},${prev.y}Q${prev.x},${prev.y},${mx},${my}L${pt.x},${pt.y}`}
                            stroke={shape.color}
                            strokeWidth={w}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fill="none"
                        />
                    )
                })}
            </g>
        )
    }

    return <g key={shape._id}>{renderInner()}</g>
}

function VectorLayer({
    layer,
    canvasWidth,
    canvasHeight,
    activeShape,
    selectedElementIds,
    selectionBounds,
    selectionRotation,
    selectionCenter,
    selectionPivot,
    cropTargetId,
    cropEditRect,
    cropTargetRotation,
    cropTargetCenter,
    snapLines,
    className,
}) {
    const { visible, opacity, elements, _id: layerId } = layer

    if (!visible) return null

    const lf = normalizeLayerFilters(layer)
    const filterId = layerFilterDomId(layerId)
    const useFilter = layerNeedsSvgFilter(layer.filters)
    const blendCss = blendModeToCss(lf.blendMode)
    const divStyle = useMemo(() => ({
        opacity,
        overflow: 'visible',
        ...(blendCss ? { mixBlendMode: blendCss } : {}),
    }), [opacity, blendCss])

    const selSet = selectedElementIds || []
    const isCropping = !!cropTargetId
    const hasSelection = !isCropping && selSet.length > 0 && selectionBounds

    const handles = hasSelection ? getHandlePositions(selectionBounds) : []
    const rotHandlePos = hasSelection ? getRotationHandlePos(selectionBounds) : null
    const selRotation = selectionRotation ?? 0
    const selCenter = selectionCenter ?? (hasSelection ? getBoundsCenter(selectionBounds) : null)
    const selPivot = selectionPivot ?? selCenter

    const cropHandles = isCropping && cropEditRect ? getCropHandlePositions(cropEditRect) : []

    return (
        <div className={cn("absolute inset-0 pointer-events-none", className)} style={divStyle}>
            <svg
                viewBox={`0 0 ${canvasWidth} ${canvasHeight}`}
                className="absolute inset-0 w-full h-full"
                preserveAspectRatio="none"
                style={{ overflow: 'visible' }}
            >
                {useFilter && (
                    <SvgLayerFilterDefs
                        filterId={filterId}
                        canvasWidth={canvasWidth}
                        canvasHeight={canvasHeight}
                        filters={layer.filters}
                    />
                )}
                <g filter={useFilter ? `url(#${filterId})` : undefined}>
                    {(elements || []).map(el => {
                        if (el.kind === 'image') {
                            const elIsCropTarget = cropTargetId === el._id
                            return renderImageSvg(el, canvasWidth, canvasHeight, {
                                isCropTarget: elIsCropTarget,
                                cropEditRect: elIsCropTarget ? cropEditRect : null,
                            })
                        }
                        if (el.kind === 'path') return renderPathSvg(el, canvasWidth, canvasHeight)
                        return null
                    })}
                    {activeShape && renderPathSvg(
                        { ...activeShape, _id: '__active', rotation: 0 },
                        canvasWidth, canvasHeight
                    )}
                </g>

                {hasSelection && selPivot && (
                    <g transform={`rotate(${selRotation} ${selPivot.x} ${selPivot.y})`}>
                        <rect
                            x={selectionBounds.minX}
                            y={selectionBounds.minY}
                            width={selectionBounds.maxX - selectionBounds.minX}
                            height={selectionBounds.maxY - selectionBounds.minY}
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            strokeDasharray="6 3"
                            rx={2}
                        />
                        {handles.map(h => (
                            <rect
                                key={h.id}
                                x={h.x - HANDLE_SIZE / 2}
                                y={h.y - HANDLE_SIZE / 2}
                                width={HANDLE_SIZE}
                                height={HANDLE_SIZE}
                                fill="white"
                                stroke="#3b82f6"
                                strokeWidth={1.5}
                                rx={1}
                            />
                        ))}
                        {rotHandlePos && (
                            <>
                                <line
                                    x1={(selectionBounds.minX + selectionBounds.maxX) / 2}
                                    y1={selectionBounds.minY}
                                    x2={rotHandlePos.x}
                                    y2={rotHandlePos.y}
                                    stroke="#3b82f6"
                                    strokeWidth={1.5}
                                />
                                <circle
                                    cx={rotHandlePos.x}
                                    cy={rotHandlePos.y}
                                    r={ROTATION_HANDLE_RADIUS}
                                    fill="white"
                                    stroke="#3b82f6"
                                    strokeWidth={1.5}
                                />
                            </>
                        )}
                    </g>
                )}

                {isCropping && cropEditRect && (() => {
                    const cropRot = cropTargetRotation ?? 0
                    const cropCenter = cropTargetCenter ?? getBoundsCenter(cropEditRect)
                    return (
                        <g transform={cropRot !== 0 ? `rotate(${cropRot} ${cropCenter.x} ${cropCenter.y})` : undefined}>
                            <rect
                                x={cropEditRect.minX} y={cropEditRect.minY}
                                width={cropEditRect.maxX - cropEditRect.minX}
                                height={cropEditRect.maxY - cropEditRect.minY}
                                fill="none"
                                stroke="#f59e0b"
                                strokeWidth={2}
                                rx={1}
                            />
                            {cropHandles.map(h => (
                                <rect
                                    key={h.id}
                                    x={h.x - CROP_HANDLE_SIZE / 2}
                                    y={h.y - CROP_HANDLE_SIZE / 2}
                                    width={CROP_HANDLE_SIZE}
                                    height={CROP_HANDLE_SIZE}
                                    fill="white"
                                    stroke="#f59e0b"
                                    strokeWidth={1.5}
                                    rx={1}
                                />
                            ))}
                        </g>
                    )
                })()}

                {snapLines && snapLines.map((line, i) => (
                    line.axis === 'x'
                        ? <line key={`snap-${i}`} x1={line.pos} y1={0} x2={line.pos} y2={canvasHeight}
                            stroke="#f43f5e" strokeWidth={1} strokeDasharray="4 3" />
                        : <line key={`snap-${i}`} x1={0} y1={line.pos} x2={canvasWidth} y2={line.pos}
                            stroke="#f43f5e" strokeWidth={1} strokeDasharray="4 3" />
                ))}
            </svg>
        </div>
    )
}

export default memo(VectorLayer)

export {
    pointsToSvgPath,
    getElementBounds,
    getElementLocalBounds,
    getMultiElementBounds,
    getHandlePositions,
    getRotationHandlePos,
    getBoundsCenter,
    getCropRect,
    getCropHandlePositions,
    hasCrop,
    HANDLE_SIZE,
    CROP_HANDLE_SIZE,
    ROTATION_HANDLE_OFFSET,
    ROTATION_HANDLE_RADIUS,
}
